'use client';

import type { InfiniteData} from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';
import produce from 'immer';
import { useAuth } from 'react-oidc-context';

import { createAnswerQuestion, createComment, createQuestion, createReview } from '../../../apis/feedbacks';
import {
  createClientComment,
  createClientQuestion,
  createClientReview,
} from '../../../helpers/feedbacks/clientSideFeedback';
import { createClientReply } from '../../../helpers/feedbacks/clientSideReply';
import type { FeedbackPageType, FeedbackTopicType, NewCommentType, TopicInfoType } from '../../../types/feedbacks';
import { useFeedbacks } from '../shared/useFeedbacks';

type FeedbackCreateMutation = {
  topicType: FeedbackTopicType;
  topicInfo: TopicInfoType;
  onSuccess?: (data: AxiosResponse, variables: NewCommentType) => void;
  onMutate?: () => void;
};

export const useFeedbackCreateMutation = (args: FeedbackCreateMutation) => {
  const { onSuccess, onMutate, topicInfo, topicType } = args;
  const { topicId, topicSlug, topicTitle } = topicInfo;
  const { filter, sort } = useFeedbacks();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const accessToken = user?.access_token as string;

  return useMutation(
    (newComment: NewCommentType) => {
      if (newComment.isQuestion) {
        return createQuestion(
          topicId,
          newComment.topicType,
          newComment.content,
          newComment.ranges,
          newComment.mentions,
          accessToken,
          topicSlug,
          topicTitle,
        );
      } else if (newComment.feedbackToReply) {
        if (newComment.feedbackToReply.type === 'Question') {
          return createAnswerQuestion(
            newComment.feedbackToReply.id,
            topicId,
            newComment.topicType,
            newComment.content,
            newComment.ranges,
            newComment.mentions,
            accessToken,
            topicSlug,
            topicTitle,
          );
        }
      }
      switch (newComment.topicType) {
        case 'Article':
          return createComment(
            topicId,
            newComment.topicType,
            newComment.content,
            newComment.ranges,
            newComment.mentions,
            accessToken,
            topicSlug,
            topicTitle,
            newComment.feedbackToReply?.id,
          );

        default:
          return newComment?.feedbackToReply
            ? createComment(
                topicId,
                newComment.topicType,
                newComment.content,
                newComment.ranges,
                newComment.mentions,
                accessToken,
                topicSlug,
                topicTitle,
                newComment.feedbackToReply?.id,
              )
            : createReview(
                topicId,
                newComment.topicType,
                newComment.content,
                newComment.ranges,
                newComment.mentions,
                accessToken,
                topicSlug,
                topicTitle,
                newComment?.score || 0,
              );
      }
    },
    {
      onSuccess: (data, variables) => {
        onSuccess?.(data, variables);
      },
      onMutate: async (newComment) => {
        onMutate && onMutate();
        const key = ['feedbacks', topicId, filter, sort];
        await queryClient.cancelQueries(key);
        const previousFeedbacks = queryClient.getQueryData<InfiniteData<FeedbackPageType>>(key);
        const userData = {
          userId: user?.profile.sub as string,
          avatarId: user?.profile.picture,
          fullName: user?.profile.name as string,
          username: user?.profile.name as string,
          displayFullName: true,
        };
        if (!newComment.feedbackToReply) {
          if (previousFeedbacks) {
            queryClient.setQueryData<{ pages: FeedbackPageType[] }>(key, () => {
              const nextState = produce(previousFeedbacks, (draftState) => {
                const shareFeedbackData = {
                  content: newComment.content,
                  ranges: newComment.ranges,
                  user: userData,
                  topicType: newComment.topicType,
                };
                if (newComment.isQuestion) {
                  const question = createClientQuestion({
                    ...shareFeedbackData,
                  });
                  draftState.pages[0].allFeedback.unshift(question);
                } else {
                  if (newComment.topicType === 'Article' || newComment.feedbackToReply) {
                    const comment = createClientComment({
                      ...shareFeedbackData,
                    });
                    draftState.pages[0].allFeedback.unshift(comment);
                  } else if (newComment.content) {
                    const comment = createClientReview({
                      ...shareFeedbackData,
                      score: newComment.score!,
                    });
                    draftState.pages[0].allFeedback.unshift(comment);
                  }
                }
              });
              return nextState;
            });
          }
        } else {
          if (previousFeedbacks) {
            queryClient.setQueryData<{ pages: FeedbackPageType[] }>(key, () => {
              const nextState = produce(previousFeedbacks, (draftState) => {
                const parentId = newComment.feedbackToReply!.id;
                const feedback =
                  draftState.pages[newComment.feedbackToReply!.pageIndex].allFeedback[
                    newComment.feedbackToReply!.listIndex
                  ];

                createClientReply({
                  userData,
                  feedback,
                  targetId: parentId,
                  content: newComment.content,
                  ranges: newComment.ranges,
                  topicType,
                });
              });

              return nextState;
            });
          }
        }
      },
    },
  );
};
